/**
 * TanStack Query 客户端配置
 * 本地化查询客户端实例
 */

import { QueryClient } from '@tanstack/vue-query'

/**
 * 创建查询客户端实例
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 数据保持新鲜的时间（5分钟）
      staleTime: 5 * 60 * 1000,
      // 缓存时间（10分钟）
      gcTime: 10 * 60 * 1000,
      // 重试次数
      retry: 3,
      // 重试延迟
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // 窗口重新获得焦点时重新获取数据
      refetchOnWindowFocus: false,
      // 网络重新连接时重新获取数据
      refetchOnReconnect: true,
      // 组件挂载时重新获取数据
      refetchOnMount: true
    },
    mutations: {
      // 变更重试次数
      retry: 1,
      // 变更重试延迟
      retryDelay: 1000
    }
  }
})

/**
 * 查询键工厂
 * 用于生成标准化的查询键
 */
export const queryKeys = {
  // 用户相关
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.users.lists(), { filters }] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.users.details(), id] as const
  },

  // 权限相关
  permissions: {
    all: ['permissions'] as const,
    lists: () => [...queryKeys.permissions.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.permissions.lists(), { filters }] as const
  },

  // 角色相关
  roles: {
    all: ['roles'] as const,
    lists: () => [...queryKeys.roles.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.roles.lists(), { filters }] as const,
    details: () => [...queryKeys.roles.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.roles.details(), id] as const
  },

  // 模块相关
  modules: {
    all: ['modules'] as const,
    configs: () => [...queryKeys.modules.all, 'config'] as const,
    config: (moduleName: string) => [...queryKeys.modules.configs(), moduleName] as const
  }
}

/**
 * 查询工具函数
 */
export const queryUtils = {
  /**
   * 使查询无效
   */
  invalidateQueries: (queryKey: any[]) => {
    return queryClient.invalidateQueries({ queryKey })
  },

  /**
   * 移除查询
   */
  removeQueries: (queryKey: any[]) => {
    return queryClient.removeQueries({ queryKey })
  },

  /**
   * 设置查询数据
   */
  setQueryData: (queryKey: any[], data: any) => {
    return queryClient.setQueryData(queryKey, data)
  },

  /**
   * 获取查询数据
   */
  getQueryData: (queryKey: any[]) => {
    return queryClient.getQueryData(queryKey)
  },

  /**
   * 预取查询
   */
  prefetchQuery: (options: any) => {
    return queryClient.prefetchQuery(options)
  },

  /**
   * 清除所有查询
   */
  clear: () => {
    return queryClient.clear()
  }
}

export default queryClient
